import React, { useState } from 'react';
import './TopAdUnitsTable.css';

const TopAdUnitsTable = ({ adUnits }) => {
  const [sortField, setSortField] = useState('revenue');
  const [sortDirection, setSortDirection] = useState('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 50;

  if (!adUnits || adUnits.length === 0) {
    return (
      <div className="top-ad-units-table">
        <div className="table-header">
          <h3>Top Ad Units</h3>
        </div>
        <div className="no-table-data">
          <p>No ad unit data available</p>
        </div>
      </div>
    );
  }

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const formatNumber = (num) => {
    if (num >= 1000000) {
      return `${(num / 1000000).toFixed(1)}M`;
    }
    if (num >= 1000) {
      return `${(num / 1000).toFixed(1)}K`;
    }
    return num.toLocaleString();
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const getSortIcon = (field) => {
    if (sortField !== field) {
      return (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="sort-icon inactive">
          <path d="M8 9l4-4 4 4M8 15l4 4 4-4" />
        </svg>
      );
    }
    
    return sortDirection === 'asc' ? (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="sort-icon active">
        <path d="M8 15l4-4 4 4" />
        <path d="M16 11H8" />
      </svg>
    ) : (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="sort-icon active">
        <path d="M8 9l4 4 4-4" />
        <path d="M16 13H8" />
      </svg>
    );
  };

  // Sort ad units based on current sort field and direction
  const sortedAdUnits = [...adUnits].sort((a, b) => {
    let aValue, bValue;
    
    switch (sortField) {
      case 'adUnitName':
        aValue = a.adUnitName || '';
        bValue = b.adUnitName || '';
        return sortDirection === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
      case 'revenue':
        aValue = a.revenue || 0;
        bValue = b.revenue || 0;
        return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
      case 'rpm':
        aValue = a.rpm || 0;
        bValue = b.rpm || 0;
        return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
      case 'cpm':
        aValue = a.cpm || 0;
        bValue = b.cpm || 0;
        return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
      case 'adImpressions':
        aValue = a.adImpressions || 0;
        bValue = b.adImpressions || 0;
        return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
      case 'viewability':
        aValue = a.viewabilityRate || 0;
        bValue = b.viewabilityRate || 0;
        return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
      default:
        return 0;
    }
  });

  // Pagination logic
  const totalPages = Math.ceil(sortedAdUnits.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedAdUnits = sortedAdUnits.slice(startIndex, endIndex);
  const totalItems = sortedAdUnits.length;

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  return (
    <div className="top-ad-units-table">
      <div className="table-header">
        <h3>Top Ad Units</h3>
      </div>
      <div className="table-container">
        <table className="ad-units-table">
          <thead>
            <tr>
              <th onClick={() => handleSort('adUnitName')} className="sortable">
                <div className="th-content">
                  Ad Unit
                  {getSortIcon('adUnitName')}
                </div>
              </th>
              <th onClick={() => handleSort('revenue')} className="sortable">
                <div className="th-content">
                  Revenue
                  {getSortIcon('revenue')}
                </div>
              </th>
              <th onClick={() => handleSort('rpm')} className="sortable">
                <div className="th-content">
                  RPM
                  {getSortIcon('rpm')}
                </div>
              </th>
              <th onClick={() => handleSort('cpm')} className="sortable">
                <div className="th-content">
                  CPM
                  {getSortIcon('cpm')}
                </div>
              </th>
              <th onClick={() => handleSort('adImpressions')} className="sortable">
                <div className="th-content">
                  Ad Impressions
                  {getSortIcon('adImpressions')}
                </div>
              </th>
              <th onClick={() => handleSort('viewability')} className="sortable">
                <div className="th-content">
                  Viewability
                  {getSortIcon('viewability')}
                </div>
              </th>
            </tr>
          </thead>
          <tbody>
            {paginatedAdUnits.map((adUnit, index) => (
              <tr key={`${adUnit.adUnitName}-${index}`}>
                <td className="ad-unit-name">
                  {adUnit.adUnitName || 'Unknown'}
                  {adUnit.isAutoAd && <span className="auto-ad-badge" title="Auto Ad">🤖</span>}
                </td>
                <td className="revenue-cell">{formatCurrency(adUnit.revenue || 0)}</td>
                <td className="rpm-cell">${(adUnit.rpm || 0).toFixed(2)}</td>
                <td className="cpm-cell">${(adUnit.cpm || 0).toFixed(2)}</td>
                <td className="impressions-cell">{formatNumber(adUnit.adImpressions || 0)}</td>
                <td className="viewability-cell">
                  {adUnit.viewabilityRate !== null && adUnit.viewabilityRate !== undefined 
                    ? `${(adUnit.viewabilityRate).toFixed(1)}%` 
                    : 'N/A'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {totalPages > 1 && (
        <div className="table-pagination">
          <button 
            onClick={handlePreviousPage} 
            disabled={currentPage === 1}
            className="pagination-button"
          >
            Previous
          </button>
          <span className="pagination-info">
            Page {currentPage} of {totalPages} ({totalItems} total)
          </span>
          <button 
            onClick={handleNextPage} 
            disabled={currentPage === totalPages}
            className="pagination-button"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default TopAdUnitsTable;

